/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// Launch step 3 - name & size, GPU/CPU/RAM resources, NFS volume, access
// (SSH key + exposed ports), environment/startup command and billing period.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Chip } from '../../console/ui';
import { CloseIc, PlusIc } from '../../console/icons';
import { Callout } from '../Dialog';
import { GPUAvailability, GPUPricing, Project, SSHKey, Volume } from '../../../types';
import { PodForm } from './types';
import { BILLING_PERIODS } from './catalog';

interface ConfigStepProps {
  form: PodForm;
  setForm: React.Dispatch<React.SetStateAction<PodForm>>;
  projects: Project[];
  onProjectChange: (projectId: string) => void;
  selectedGPU: GPUPricing | null;
  gpuAvailability?: GPUAvailability;
  includedCpu: number;
  includedMemory: number;
  effectiveVram: number;
  cpuMax: number;
  memoryMax: number;
  cpuFree: number | null;
  memoryFree: number | null;
  noCluster: boolean;
  volumes: Volume[];
  selectedVolume: Volume | null;
  onSelectVolume: (v: Volume | null) => void;
  mountPath: string;
  onMountPathChange: (p: string) => void;
  onCreateVolume: () => void;
  sshKeys: SSHKey[];
  onCreateSSHKey: () => void;
  isTemplateCommand: boolean;
  setIsTemplateCommand: (v: boolean) => void;
  onCompareSavings: () => void;
}

const Section: React.FC<{ id: string; title: React.ReactNode; action?: React.ReactNode; children: React.ReactNode }> = ({
  id,
  title,
  action,
  children,
}) => (
  <section className="ps-card p-5 flex flex-col gap-3.5" aria-labelledby={id}>
    <div className="flex flex-wrap justify-between items-center gap-3">
      <h2 id={id} className="m-0 text-base font-medium">
        {title}
      </h2>
      {action}
    </div>
    {children}
  </section>
);

/** Keeps a free-typing string mirror of a numeric field. */
const useRaw = (value: number) => {
  const [raw, setRaw] = useState(String(value));
  useEffect(() => {
    setRaw((r) => (parseInt(r, 10) === value ? r : String(value)));
  }, [value]);
  return [raw, setRaw] as const;
};

const track = (included: number, max: number) =>
  included > 0
    ? {
        background: `linear-gradient(to right, #22C55E 0%, #22C55E ${(included / max) * 100}%, #232330 ${(included / max) * 100}%, #232330 100%)`,
      }
    : undefined;

const rangeCls = 'w-full h-2 rounded-lg appearance-none cursor-pointer bg-ps-border accent-[#F97316]';
const numCls = 'ps-input w-20 text-center';

const ConfigStep: React.FC<ConfigStepProps> = (p) => {
  const { form, setForm, selectedGPU } = p;
  const [rawCpu, setRawCpu] = useRaw(form.cpu_cores);
  const [rawMemory, setRawMemory] = useRaw(form.memory_gb);
  const [rawGpuCount, setRawGpuCount] = useRaw(form.gpu_count);
  const [rawGpuCores, setRawGpuCores] = useRaw(form.gpu_cores);
  const [newPort, setNewPort] = useState('');

  const maxGpus = Math.max(1, Math.floor(p.gpuAvailability?.available_gpus ?? 8));

  const addPort = () => {
    const port = parseInt(newPort, 10);
    if (port && port >= 1 && port <= 65535 && !form.ports.includes(port)) {
      setForm((prev) => ({ ...prev, ports: [...prev.ports, port].sort((a, b) => a - b) }));
      setNewPort('');
    }
  };

  // GPU utilisation donut (already booked / your request / free)
  const donut = (() => {
    const totalGpus = p.gpuAvailability?.total_gpus ?? 1;
    const availableGpus = p.gpuAvailability?.available_gpus ?? 1;
    const bookedFraction = totalGpus > 0 ? Math.max(0, (totalGpus - availableGpus) / totalGpus) : 0;
    const bookedPct = Math.round(bookedFraction * 100);
    const yourPct = form.gpu_cores;
    const freePct = Math.max(0, 100 - bookedPct - yourPct);
    const yourDash = Math.min(yourPct, 100 - bookedPct);
    return { bookedPct, yourPct, freePct, yourDash, overbooked: bookedPct + yourPct > 100 };
  })();

  return (
    <div className="flex flex-col gap-[18px]">
      <Section id="cfg-name" title="Name & project">
        <div className="flex flex-wrap gap-3">
          <label className="block flex-[2_1_260px]">
            <span className="ps-label">Pod name</span>
            <input
              type="text"
              className="ps-input"
              placeholder="my-training-pod"
              value={form.name}
              required
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            />
          </label>
          <label className="block flex-[1_1_200px]">
            <span className="ps-label">Project</span>
            <select
              className="ps-input"
              value={form.project_id}
              required
              onChange={(e) => p.onProjectChange(e.target.value)}
            >
              <option value="">Select a project</option>
              {p.projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </label>
          <div className="flex-[1_1_140px]">
            <span className="ps-label">Container disk</span>
            <div className="ps-input flex items-center text-ps-muted">{form.storage_gb} GB</div>
          </div>
        </div>
      </Section>

      <Section id="cfg-res" title="GPU & resources">
        {selectedGPU && (
          <Callout tone="info">
            <span className="font-semibold">{selectedGPU.gpu_type}</span> includes{' '}
            <span className="font-semibold">{p.includedCpu} vCPU</span> and{' '}
            <span className="font-semibold">{p.includedMemory} GB RAM</span> at no extra cost.
          </Callout>
        )}
        {p.noCluster && (
          <Callout tone="err">
            No cluster currently supports{' '}
            <span className="font-semibold">
              {form.gpu_count} × {form.gpu_type || 'GPU'} @ {form.gpu_cores}%
            </span>
            . Reduce the GPU count, pick a different GPU type, or join the waitlist.
          </Callout>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {/* vCPU */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-ps-muted" id="lbl-cpu">vCPU</span>
              {selectedGPU && <span className="text-xs text-ps-subtle">Included: {p.includedCpu}</span>}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <input
                  type="range"
                  aria-labelledby="lbl-cpu"
                  min="1"
                  max={p.cpuMax}
                  step="1"
                  value={form.cpu_cores}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    setForm((prev) => ({ ...prev, cpu_cores: v }));
                    setRawCpu(String(v));
                  }}
                  className={rangeCls}
                  style={selectedGPU ? track(p.includedCpu, p.cpuMax) : undefined}
                />
                <div className="flex justify-between text-xs text-ps-subtle mt-1">
                  <span>1</span>
                  <span>{p.cpuMax}</span>
                </div>
              </div>
              <input
                type="number"
                aria-label="vCPU"
                min="1"
                max={p.cpuMax}
                value={rawCpu}
                onChange={(e) => {
                  setRawCpu(e.target.value);
                  const parsed = parseInt(e.target.value, 10);
                  if (!isNaN(parsed) && parsed > 0) setForm((prev) => ({ ...prev, cpu_cores: parsed }));
                }}
                onBlur={() => {
                  const clamped = Math.max(1, Math.min(p.cpuMax, parseInt(rawCpu, 10) || 1));
                  setForm((prev) => ({ ...prev, cpu_cores: clamped }));
                  setRawCpu(String(clamped));
                }}
                className={numCls}
              />
            </div>
            {selectedGPU && form.cpu_cores > p.includedCpu && (
              <p className="m-0 mt-2 text-xs text-ps-warn">
                <span className="font-semibold">+{form.cpu_cores - p.includedCpu} extra</span> (additional charges apply)
              </p>
            )}
            {p.cpuFree !== null && (
              <p className={`m-0 mt-1 text-xs ${form.cpu_cores > p.cpuFree ? 'text-ps-err' : 'text-ps-subtle'}`}>
                {form.cpu_cores > p.cpuFree
                  ? `Only ${p.cpuFree} vCPU free right now - launch may fail until capacity opens up.`
                  : `${p.cpuFree} vCPU free now`}
              </p>
            )}
          </div>

          {/* Memory */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-ps-muted" id="lbl-mem">Memory (GB)</span>
              {selectedGPU && <span className="text-xs text-ps-subtle">Included: {p.includedMemory} GB</span>}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <input
                  type="range"
                  aria-labelledby="lbl-mem"
                  min="1"
                  max={p.memoryMax}
                  step="1"
                  value={form.memory_gb}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    setForm((prev) => ({ ...prev, memory_gb: v }));
                    setRawMemory(String(v));
                  }}
                  className={rangeCls}
                  style={selectedGPU ? track(p.includedMemory, p.memoryMax) : undefined}
                />
                <div className="flex justify-between text-xs text-ps-subtle mt-1">
                  <span>1</span>
                  <span>{p.memoryMax}</span>
                </div>
              </div>
              <input
                type="number"
                aria-label="Memory in GB"
                min="1"
                max={p.memoryMax}
                value={rawMemory}
                onChange={(e) => {
                  setRawMemory(e.target.value);
                  const parsed = parseInt(e.target.value, 10);
                  if (!isNaN(parsed) && parsed > 0) setForm((prev) => ({ ...prev, memory_gb: parsed }));
                }}
                onBlur={() => {
                  const clamped = Math.max(1, Math.min(p.memoryMax, parseInt(rawMemory, 10) || 1));
                  setForm((prev) => ({ ...prev, memory_gb: clamped }));
                  setRawMemory(String(clamped));
                }}
                className={numCls}
              />
            </div>
            {selectedGPU && form.memory_gb > p.includedMemory && (
              <p className="m-0 mt-2 text-xs text-ps-warn">
                <span className="font-semibold">+{form.memory_gb - p.includedMemory} GB extra</span> (additional charges apply)
              </p>
            )}
            {p.memoryFree !== null && (
              <p className={`m-0 mt-1 text-xs ${form.memory_gb > p.memoryFree ? 'text-ps-err' : 'text-ps-subtle'}`}>
                {form.memory_gb > p.memoryFree
                  ? `Only ${p.memoryFree} GB free right now - launch may fail until capacity opens up.`
                  : `${p.memoryFree} GB free now`}
              </p>
            )}
          </div>

          {selectedGPU && (
            <>
              <div>
                <span className="ps-label">GPU type</span>
                <div className="ps-input flex items-center text-ps-muted">{form.gpu_type}</div>
              </div>

              {/* GPU count */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-ps-muted" id="lbl-gpus">Number of GPUs</span>
                  <span className="text-xs text-ps-subtle">
                    Available: {parseFloat((p.gpuAvailability?.available_gpus ?? 0).toFixed(2))}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <input
                      type="range"
                      aria-labelledby="lbl-gpus"
                      min="1"
                      max={maxGpus}
                      step="1"
                      value={form.gpu_count}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        setForm((prev) => ({ ...prev, gpu_count: v }));
                        setRawGpuCount(String(v));
                      }}
                      className={rangeCls}
                    />
                    <div className="flex justify-between text-xs text-ps-subtle mt-1">
                      <span>1</span>
                      <span>{maxGpus}</span>
                    </div>
                  </div>
                  <input
                    type="number"
                    aria-label="Number of GPUs"
                    min="1"
                    max={maxGpus}
                    value={rawGpuCount}
                    onChange={(e) => {
                      setRawGpuCount(e.target.value);
                      const parsed = parseInt(e.target.value, 10);
                      if (!isNaN(parsed) && parsed > 0) setForm((prev) => ({ ...prev, gpu_count: parsed }));
                    }}
                    onBlur={() => {
                      const clamped = Math.max(1, Math.min(maxGpus, parseInt(rawGpuCount, 10) || 1));
                      setForm((prev) => ({ ...prev, gpu_count: clamped }));
                      setRawGpuCount(String(clamped));
                    }}
                    className={numCls}
                  />
                </div>
              </div>

              {/* GPU share */}
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-ps-muted" id="lbl-gpupct">GPU memory/cores per GPU</span>
                  <span className="text-xs font-semibold text-ps-accent">{form.gpu_cores}%</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <input
                      type="range"
                      aria-labelledby="lbl-gpupct"
                      min="5"
                      max="100"
                      step="5"
                      value={form.gpu_cores}
                      onChange={(e) => {
                        let value = parseInt(e.target.value, 10);
                        if (value >= 96 && value < 100) value = 100;
                        setForm((prev) => ({ ...prev, gpu_cores: value }));
                        setRawGpuCores(String(value));
                      }}
                      className={rangeCls}
                    />
                    <div className="flex justify-between text-xs text-ps-subtle mt-1">
                      <span>5%</span>
                      <span>100%</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      aria-label="GPU share percent"
                      min="5"
                      max="100"
                      step="5"
                      value={rawGpuCores}
                      onChange={(e) => {
                        setRawGpuCores(e.target.value);
                        const parsed = parseInt(e.target.value, 10);
                        if (!isNaN(parsed) && parsed > 0) setForm((prev) => ({ ...prev, gpu_cores: Math.min(100, parsed) }));
                      }}
                      onBlur={() => {
                        let value = parseInt(rawGpuCores, 10) || 5;
                        if (value >= 96 && value < 100) value = 100;
                        value = Math.max(5, Math.min(100, value));
                        setForm((prev) => ({ ...prev, gpu_cores: value }));
                        setRawGpuCores(String(value));
                      }}
                      className={numCls}
                    />
                    <span className="text-sm text-ps-muted">%</span>
                  </div>
                </div>

                <div className="mt-3 p-3 bg-ps-chrome rounded-lg border border-ps-border flex items-center gap-4">
                  <svg width="80" height="80" viewBox="0 0 36 36" className="flex-none" aria-hidden="true">
                    <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#1A1A24" strokeWidth="3.8" />
                    {donut.bookedPct > 0 && (
                      <circle
                        cx="18" cy="18" r="15.9155" fill="none" stroke="#FCD34D" strokeWidth="3.8"
                        strokeDasharray={`${donut.bookedPct} ${100 - donut.bookedPct}`}
                        strokeDashoffset={25}
                      />
                    )}
                    {donut.yourDash > 0 && (
                      <circle
                        cx="18" cy="18" r="15.9155" fill="none" stroke="#22C55E" strokeWidth="3.8"
                        strokeDasharray={`${donut.yourDash} ${100 - donut.yourDash}`}
                        strokeDashoffset={25 - donut.bookedPct}
                      />
                    )}
                    <text x="18" y="16.5" textAnchor="middle" className="text-[6px] font-bold fill-[#EDEDF0]">
                      {form.gpu_cores}%
                    </text>
                    <text x="18" y="22" textAnchor="middle" className="text-[4.5px] fill-[#A1A1AA]">
                      of GPU
                    </text>
                  </svg>
                  <div className="flex-1 flex flex-col gap-1.5 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-ps-warn flex-none" />
                      <span className="text-ps-muted">Already booked</span>
                      <span className="ml-auto font-semibold text-ps-warn">{donut.bookedPct}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-ps-ok flex-none" />
                      <span className="text-ps-muted">Your request</span>
                      <span className="ml-auto font-semibold text-ps-ok">{donut.yourPct}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-ps-raised flex-none" />
                      <span className="text-ps-muted">Free</span>
                      <span className="ml-auto font-semibold text-ps-muted">{donut.freePct}%</span>
                    </div>
                    {donut.overbooked && (
                      <p className="m-0 text-[11px] text-ps-warn">Exceeds available capacity - may queue until resources free up</p>
                    )}
                  </div>
                </div>

                <p className="m-0 mt-2 text-xs text-ps-muted">
                  {form.gpu_count === 1
                    ? form.gpu_cores === 100
                      ? `Using 1 full GPU (${selectedGPU.memory_gb} GB VRAM)`
                      : `Using ${form.gpu_cores}% of 1 GPU (~${p.effectiveVram.toFixed(1)} GB VRAM)`
                    : form.gpu_cores === 100
                      ? `Using ${form.gpu_count} full GPUs (${form.gpu_count * selectedGPU.memory_gb} GB VRAM total)`
                      : `Using ${form.gpu_cores}% of ${form.gpu_count} GPUs (~${p.effectiveVram.toFixed(1)} GB VRAM total)`}
                </p>
              </div>
            </>
          )}
        </div>
      </Section>

      <Section
        id="cfg-vol"
        title="Mount an NFS volume"
        action={
          <div className="flex items-center gap-4 text-sm">
            <Link to="/volumes" className="ps-link">
              Manage volumes
            </Link>
            <button type="button" className="ps-link disabled:opacity-50" onClick={p.onCreateVolume} disabled={!form.project_id}>
              + Create volume
            </button>
          </div>
        }
      >
        <p className="m-0 text-[13px] text-ps-muted">Data on a volume survives pod restarts. Optional.</p>
        {!form.project_id ? (
          <div className="rounded-lg border border-dashed border-ps-strong p-5 text-center text-sm text-ps-muted">
            Select a project first to see available volumes.
          </div>
        ) : p.volumes.length === 0 ? (
          <div className="rounded-lg border border-dashed border-ps-strong p-5 text-center text-sm text-ps-muted">
            No available NFS volumes in this project.{' '}
            <button type="button" className="ps-link" onClick={p.onCreateVolume}>
              Create your first volume
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2" role="radiogroup" aria-label="NFS volume">
            <label
              className={`flex items-center gap-3 min-h-[44px] px-3.5 py-2 rounded-lg border cursor-pointer ${
                !p.selectedVolume ? 'border-ps-accent bg-ps-accent-soft' : 'border-ps-border hover:border-ps-strong'
              }`}
            >
              <input type="radio" name="volume" checked={!p.selectedVolume} onChange={() => p.onSelectVolume(null)} className="accent-[#F97316]" />
              <span className="flex-1 font-medium">No volume</span>
              <span className="text-[13px] text-ps-muted">Ephemeral storage only</span>
            </label>
            {p.volumes.map((volume) => (
              <label
                key={volume.id}
                className={`flex items-center gap-3 min-h-[44px] px-3.5 py-2 rounded-lg border cursor-pointer ${
                  p.selectedVolume?.id === volume.id ? 'border-ps-accent bg-ps-accent-soft' : 'border-ps-border hover:border-ps-strong'
                }`}
              >
                <input
                  type="radio"
                  name="volume"
                  checked={p.selectedVolume?.id === volume.id}
                  onChange={() => p.onSelectVolume(volume)}
                  className="accent-[#F97316]"
                />
                <span className="flex-1 min-w-0">
                  <span className="font-medium">{volume.name}</span>
                  {(volume.nfs_server_ip || volume.nfs_server) && (
                    <span className="hidden sm:inline ml-2 text-xs text-ps-subtle">NFS {volume.nfs_server_ip || volume.nfs_server}</span>
                  )}
                </span>
                <span className="text-[13px] text-ps-muted">{volume.quota_gb || volume.size_gb || 0} GB</span>
              </label>
            ))}
            {p.selectedVolume && (
              <label className="block pt-2">
                <span className="ps-label">Mount path inside pod</span>
                <input
                  type="text"
                  className="ps-input font-mono"
                  placeholder="/data"
                  value={p.mountPath}
                  onChange={(e) => p.onMountPathChange(e.target.value)}
                />
                <span className="block mt-1 text-xs text-ps-subtle">The volume will be mounted at this path inside your container.</span>
              </label>
            )}
          </div>
        )}
      </Section>

      <Section id="cfg-access" title="Access">
        <div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="block flex-[1_1_260px]">
              <span className="ps-label">SSH key (optional)</span>
              <select
                className="ps-input"
                value={form.ssh_key_id}
                onChange={(e) => setForm((prev) => ({ ...prev, ssh_key_id: e.target.value }))}
              >
                <option value="">No SSH access</option>
                {p.sshKeys.map((key) => (
                  <option key={key.id} value={key.id}>
                    {key.name}
                    {key.is_default ? ' (default)' : ''} · {String(key.key_type).toUpperCase()}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="ps-btn2" onClick={p.onCreateSSHKey}>
              <PlusIc className="w-4 h-4" />
              Add new key
            </button>
          </div>
          <p className="m-0 mt-2 text-xs text-ps-subtle">
            SSH keys can only be added during pod creation. Port 22 is exposed automatically when a key is selected.
          </p>
        </div>

        <div className="border-t border-ps-border pt-3.5">
          <span className="ps-label">Exposed ports</span>
          <div className="flex gap-2">
            <input
              type="number"
              className="ps-input flex-1"
              placeholder="Port number, e.g. 8080"
              aria-label="Port to expose"
              min="1"
              max="65535"
              value={newPort}
              onChange={(e) => setNewPort(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addPort();
                }
              }}
            />
            <button type="button" className="ps-btn2" onClick={addPort}>
              Add port
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mt-2.5">
            {form.ports.map((port) => (
              <span key={port} className="inline-flex items-center gap-1 pl-3 pr-1 min-h-[32px] rounded-full bg-ps-raised text-sm">
                {port}
                <button
                  type="button"
                  aria-label={`Remove port ${port}`}
                  onClick={() => setForm((prev) => ({ ...prev, ports: prev.ports.filter((x) => x !== port) }))}
                  className="w-7 h-7 grid place-items-center rounded-full text-ps-subtle hover:text-ps-err"
                >
                  <CloseIc className="w-3.5 h-3.5" />
                </button>
              </span>
            ))}
            {form.ports.length === 0 && <p className="m-0 text-sm text-ps-subtle">No ports exposed. Add a port to make it accessible.</p>}
          </div>
          <p className="m-0 mt-2 text-xs text-ps-subtle">
            Container ports are mapped to public URLs; you get the endpoint details once the pod is created.
          </p>
        </div>
      </Section>

      <Section
        id="cfg-env"
        title="Environment & startup"
        action={
          <button
            type="button"
            className="ps-link text-sm"
            onClick={() =>
              setForm((prev) => ({
                ...prev,
                env_vars: { ...prev.env_vars, [`ENV_${Object.keys(prev.env_vars).length + 1}`]: '' },
              }))
            }
          >
            + Add variable
          </button>
        }
      >
        <div className="flex flex-col gap-2">
          {Object.entries(form.env_vars).map(([key, value]) => (
            <div key={key} className="flex gap-2">
              <input
                type="text"
                aria-label="Variable name"
                className="ps-input font-mono flex-[1_1_140px]"
                placeholder="KEY"
                value={key}
                onChange={(e) =>
                  setForm((prev) => {
                    const next = { ...prev.env_vars };
                    delete next[key];
                    next[e.target.value] = value;
                    return { ...prev, env_vars: next };
                  })
                }
              />
              <input
                type="text"
                aria-label="Variable value"
                className="ps-input font-mono flex-[2_1_200px]"
                placeholder="value"
                value={value}
                onChange={(e) => setForm((prev) => ({ ...prev, env_vars: { ...prev.env_vars, [key]: e.target.value } }))}
              />
              <button
                type="button"
                aria-label={`Remove ${key}`}
                onClick={() =>
                  setForm((prev) => {
                    const next = { ...prev.env_vars };
                    delete next[key];
                    return { ...prev, env_vars: next };
                  })
                }
                className="w-10 min-h-[40px] grid place-items-center rounded-md text-ps-subtle hover:text-ps-err hover:bg-ps-hover flex-none"
              >
                <CloseIc className="w-4 h-4" />
              </button>
            </div>
          ))}
          {Object.keys(form.env_vars).length === 0 && <p className="m-0 text-sm text-ps-subtle">No environment variables.</p>}
        </div>

        <div className="border-t border-ps-border pt-3.5">
          <label className="block">
            <span className="ps-label">
              Entry point command <span className="text-ps-subtle">(optional)</span>
            </span>
            {p.isTemplateCommand && form.entry_point_command && (
              <Callout tone="warn" title="Template default command" className="mb-2">
                This command is required by the template for proper initialization. Modifying or removing it may cause
                deployment failure. Change with caution.
              </Callout>
            )}
            <input
              type="text"
              className={`ps-input font-mono ${p.isTemplateCommand && form.entry_point_command ? '!border-[#4A3A12]' : ''}`}
              placeholder="python app.py or /bin/sh -c 'python app.py'"
              value={form.entry_point_command}
              onChange={(e) => {
                const v = e.target.value;
                setForm((prev) => ({ ...prev, entry_point_command: v }));
                if (!v.trim() && p.isTemplateCommand) p.setIsTemplateCommand(false);
              }}
            />
            <span className="block mt-1 text-xs text-ps-subtle">Overrides the container's default entry point.</span>
          </label>
        </div>
      </Section>

      <Section
        id="cfg-billing"
        title="Billing period"
        action={
          <button type="button" className="ps-link text-sm" onClick={p.onCompareSavings}>
            Compare savings
          </button>
        }
      >
        <div className="flex flex-wrap gap-2">
          {BILLING_PERIODS.map((period) => (
            <Chip
              key={period.value}
              on={form.billing_period === period.value}
              onClick={() => setForm((prev) => ({ ...prev, billing_period: period.value }))}
            >
              {period.label}
            </Chip>
          ))}
        </div>
      </Section>
    </div>
  );
};

export default ConfigStep;
